import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { IoIosArrowBack } from 'react-icons/io'
import { SlLike, SlDislike } from 'react-icons/sl'
import { FaRegCommentDots } from 'react-icons/fa'
import { MdOutlineVerified } from 'react-icons/md'
import * as normalActions from '../store/actions'
import * as authAction from '../store/authAction'
import * as likeActions from '../data/like_data'
import * as followActions from '../data/follow_data'
import CommentThread from '../comps/commentThread'
import CommentComposer from '../comps/commentComposer'
import { SpinerLoad } from '../comps/comps'
import { useAuthGate } from '../comps/authPrompt'
import { isPhotoPost, isShortPost, leavePath, mediaPath } from '../utils/mediaRoute'
import { parsePostImages, uploadUrl } from '../utils/postImages'
import { commentFromSearch, scrollToComment } from '../utils/commentFocus'
import UserAvatar from '../comps/userAvatar'
import '../styles/post.css'

const photoFiles = (image, thumbnail) => {
  const fromImage = parsePostImages(image)
  if (fromImage.length) return fromImage
  return parsePostImages(thumbnail).filter(name => name && !/^\d+$/.test(name))
}

const uniquePosts = (list) => {
  const seen = new Set()
  return (list || []).filter(item => {
    const id = String(item && item.id || '')
    if (!id || seen.has(id)) return false
    seen.add(id)
    return true
  })
}

const PostImages = ({ image, thumbnail, title }) => {
  const scroller = useRef(null)
  const [index, setIndex] = useState(0)
  const urls = photoFiles(image, thumbnail).map(uploadUrl)

  if (!urls.length) return <div className="postImagesEmpty">No photo</div>

  return (
    <div className="postImagesWrap">
      <div
        className="postImages"
        ref={scroller}
        onScroll={() => {
          const node = scroller.current
          if (!node || !urls.length) return
          setIndex(Math.round(node.scrollLeft / Math.max(node.clientWidth, 1)))
        }}
      >
        {urls.map((src, i) => (
          <img key={`${src}-${i}`} src={src} alt={title || `Photo ${i + 1}`} />
        ))}
      </div>
      {urls.length > 1 && (
        <div className="postImageDots">
          {urls.map((_, i) => (
            <button
              key={i}
              type="button"
              className={i === index ? 'isActive' : ''}
              aria-label={`Photo ${i + 1}`}
              onClick={() => {
                const node = scroller.current
                if (node) node.scrollTo({ left: i * node.clientWidth, behavior: 'smooth' })
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const PostSlide = ({ post, onOpenComments, requireAuth }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const postId = String(post.id || '')
  const userid = useSelector(state => state.authReduse.id)
  const followList = useSelector(state => state.followReduse.follow || [])
  const like = useSelector(state => state.UpdsReduse.upds.filter(item => String(item.postid) === postId && item.type === 'p'))
  const dislike = useSelector(state => state.UpdsReduse.upds.filter(item => String(item.postid) === postId && item.type === 'd'))
  const likeL = useSelector(state => state.UpdsReduse.upds.find(item => item.userid === userid && String(item.postid) === postId && item.type === 'p'))
  const dislikeL = useSelector(state => state.UpdsReduse.upds.find(item => item.userid === userid && String(item.postid) === postId && item.type === 'd'))
  const commentCount = useSelector(state => state.CommentsReduse.Comments.filter(comm => String(comm.postid) === postId).length)
  const likeCount = like.length || Number(post.vlikes || 0)
  const dislikeCount = dislike.length || Number(post.vdislikes || 0)
  const commentsCount = commentCount || Number(post.vcomments || 0)

  return (
    <article className="postSlide">
      <div className="postAuthor" onClick={() => post.userid && navigate(`/@${post.userid}`)}>
        <UserAvatar src={post.userImg} name={post.email} alt="" />
        <div>
          <p>{post.email} {post.followers > 1 ? <MdOutlineVerified /> : null}</p>
          <span>{post.title}</span>
        </div>
        <button
          type="button"
          onClick={event => {
            event.stopPropagation()
            if (!requireAuth()) return
            dispatch(normalActions.add_to_follow(post.userid, post.email))
          }}
        >
          {followList.find(user => String(user.userid) === String(post.userid)) ? 'Following' : 'Follow'}
        </button>
      </div>

      <PostImages image={post.image} thumbnail={post.thumbnail} title={post.title} />

      <div className="postActions">
        <button type="button" className={likeL ? 'active' : ''} onClick={() => requireAuth() && dispatch(normalActions.add_to_upds('p', userid, post.id))}>
          <SlLike /> {likeCount}
        </button>
        <button type="button" className={dislikeL ? 'active' : ''} onClick={() => requireAuth() && dispatch(normalActions.add_to_upds('d', userid, post.id))}>
          <SlDislike /> {dislikeCount}
        </button>
        <button type="button" onClick={() => onOpenComments(post.id)}>
          <FaRegCommentDots /> {commentsCount}
        </button>
      </div>
      {post.description ? <p className="postCaption">{post.description}</p> : null}
    </article>
  )
}

const buildFeed = (id, seedItem, seedFeed, seedSource, fallback) => {
  const fromWatch = seedSource === 'watch' || !Array.isArray(seedFeed) || seedFeed.length === 0
  const photos = uniquePosts((seedFeed || []).filter(item => item && (isPhotoPost(item) || item.type === 'i')))
  const at = photos.findIndex(item => String(item.id) === String(id))
  if (fromWatch) return uniquePosts([seedItem, fallback].filter(item => item && item.id))
  if (at >= 0) return photos.slice(at)
  return uniquePosts([seedItem, fallback].filter(item => item && item.id))
}

const PostPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const [ready, setReady] = useState(false)
  const [showComments, setShowComments] = useState(() => Boolean(commentFromSearch(location.search)))
  const commentTarget = useRef(commentFromSearch(location.search))
  const [replyTo, setReplyTo] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const { requireAuth, promptEl } = useAuthGate()
  const scroller = useRef(null)
  const fetchingRelated = useRef(false)
  const relatedDone = useRef(false)
  const loadedIds = useRef(new Set())
  const feedRef = useRef([])
  const sessionKey = useRef(location.key)
  const selfNav = useRef(false)

  const loaded = useSelector(state => state.PostReducer.post || {})
  const userPosts = useSelector(state => state.PostReducer.userPosts || [])
  const visitedPosts = useSelector(state => state.PostReducer.vistedPost || [])
  const Comments = useSelector(state => state.CommentsReduse.Comments.filter(comm => String(comm.postid) === String(id)))
  const userid = useSelector(state => state.authReduse.id)
  const pid = useSelector(state => state.authReduse.pid)
  const userPhoto = useSelector(state => state.authReduse.photo)
  const userEmail = useSelector(state => state.authReduse.email)

  const seedItem = location.state && location.state.item
  const seedFeed = location.state && location.state.feed
  const seedSource = location.state && location.state.source

  const fallbackPost = useMemo(() => {
    if (seedItem && String(seedItem.id) === String(id)) return seedItem
    return [...userPosts, ...visitedPosts].find(item => String(item.id) === String(id)) || {}
  }, [seedItem, id, userPosts, visitedPosts])

  const [feed, setFeed] = useState(() => buildFeed(id, seedItem, seedFeed, seedSource, fallbackPost))
  feedRef.current = feed

  useEffect(() => {
    if (selfNav.current) {
      selfNav.current = false
      sessionKey.current = location.key
      return
    }
    if (sessionKey.current === location.key) return
    sessionKey.current = location.key
    relatedDone.current = false
    fetchingRelated.current = false
    loadedIds.current = new Set()
    setActiveIndex(0)
    setShowComments(false)
    setFeed(buildFeed(id, seedItem, seedFeed, seedSource, fallbackPost))
  }, [location.key, id, seedItem, seedFeed, seedSource, fallbackPost])

  useEffect(() => {
    if (!loaded || !loaded.id) return
    setFeed(current => {
      if (current.some(item => String(item.id) === String(loaded.id))) {
        return current.map(item => String(item.id) === String(loaded.id)
          ? { ...item, ...loaded, image: loaded.image || item.image, thumbnail: loaded.thumbnail || item.thumbnail }
          : item)
      }
      if (String(loaded.id) === String(id) && (isPhotoPost(loaded) || loaded.type === 'i')) {
        return uniquePosts([loaded, ...current])
      }
      return current
    })
  }, [loaded, id])

  useEffect(() => {
    if (!fallbackPost.id) return
    setFeed(current => {
      if (!current.length) return [fallbackPost]
      return current.map(item => String(item.id) === String(fallbackPost.id)
        ? { ...item, ...fallbackPost, image: fallbackPost.image || item.image, thumbnail: fallbackPost.thumbnail || item.thumbnail }
        : item)
    })
  }, [fallbackPost])

  useEffect(() => {
    const load = async () => {
      const storage = localStorage.getItem('userdata')
      if (storage) {
        const { id: authId, pid: authPid } = JSON.parse(storage)
        await dispatch(authAction.auth(authId, authPid))
        await dispatch(likeActions.get_my_likes())
        await dispatch(followActions.get_follow())
      }
      setReady(true)
    }
    load()
  }, [dispatch])

  useEffect(() => {
    if (String(loaded.id) === String(id) && loaded.type && !isPhotoPost(loaded)) {
      navigate(isShortPost(loaded) ? mediaPath(loaded) : `/video/${encodeURIComponent(id)}`, { replace: true, state: { item: loaded } })
    }
  }, [loaded.id, loaded.type, id, navigate])

  const loadPost = useCallback(async (postId, markView) => {
    if (!postId || loadedIds.current.has(`post:${postId}`)) return
    loadedIds.current.add(`post:${postId}`)
    try {
      const storage = localStorage.getItem('userdata')
      if (markView && storage && !loadedIds.current.has(`view:${postId}`)) {
        loadedIds.current.add(`view:${postId}`)
        const { id: authId } = JSON.parse(storage)
        await dispatch(normalActions.add_to_views(authId, postId))
      }
      await dispatch(likeActions.get_post(postId))
    } catch (err) {
      loadedIds.current.delete(`post:${postId}`)
    }
  }, [dispatch])

  const loadRelated = useCallback(async (fromPost) => {
    if (!fromPost || !fromPost.id || fetchingRelated.current || relatedDone.current) return
    fetchingRelated.current = true
    try {
      const rows = await dispatch(likeActions.fetchRelatedPhotos(fromPost.id, feedRef.current.map(item => item.id)))
      const have = new Set(feedRef.current.map(item => String(item.id)))
      const photos = uniquePosts((rows || []).filter(isPhotoPost).filter(item => !have.has(String(item.id))))
      if (!photos.length) {
        relatedDone.current = true
        return
      }
      setFeed(current => uniquePosts(current.concat(photos)))
    } catch (err) {
      relatedDone.current = true
    } finally {
      fetchingRelated.current = false
    }
  }, [dispatch])

  useEffect(() => {
    if (id) loadPost(id, true)
  }, [id, loadPost])

  useEffect(() => {
    const active = feed[activeIndex] || feed[0]
    if (active && active.id && String(active.id) !== String(id)) loadPost(active.id, true)
  }, [activeIndex, feed, id, loadPost])

  useEffect(() => {
    if (!feed.length) return
    if (activeIndex >= feed.length - 2) loadRelated(feed[feed.length - 1])
  }, [activeIndex, feed, loadRelated])

  const onScroll = () => {
    const node = scroller.current
    if (!node) return
    const height = Math.max(node.clientHeight, 1)
    const nextIndex = Math.max(0, Math.min(Math.max(feed.length - 1, 0), Math.round(node.scrollTop / height)))
    setActiveIndex(prev => (prev === nextIndex ? prev : nextIndex))
    if (node.scrollTop + node.clientHeight + 120 >= node.scrollHeight) {
      loadRelated(feed[feed.length - 1])
    }
  }

  useEffect(() => {
    const active = feed[activeIndex]
    if (!active || !active.id || String(active.id) === String(id)) return
    selfNav.current = true
    navigate(`/post/${encodeURIComponent(active.id)}`, {
      replace: true,
      state: {
        ...(location.state || {}),
        item: active,
        feed,
        source: seedSource || (Array.isArray(seedFeed) && seedFeed.length > 1 ? 'list' : 'watch')
      }
    })
  }, [activeIndex])

  const repliesByParent = Comments.reduce((map, comment) => {
    const parentKey = comment.parentId ? String(comment.parentId) : ''
    if (!parentKey) return map
    map[parentKey] = map[parentKey] || []
    map[parentKey].push(comment)
    return map
  }, {})
  const rootComments = Comments.filter(comment => !comment.parentId)
  const activePost = feed[activeIndex] || fallbackPost
  const slides = feed.length ? feed : [fallbackPost].filter(item => item && item.id)

  const handleAddComment = async ({ text, media }) => {
    if (!requireAuth()) return
    if ((!text && !media) || !userid || !pid || !activePost.id) return
    await dispatch(normalActions.add_to_comments(text, userid, activePost.id, userPhoto, userEmail, pid, replyTo?.id || null, media))
    setReplyTo(null)
  }

  const handleDeleteComment = comment => {
    if (!requireAuth()) return
    if (String(comment.userid) !== String(userid)) return
    dispatch(normalActions.delete_comment(comment.id, userid, pid))
  }

  const openComments = (postId) => {
    loadedIds.current.delete(`post:${postId}`)
    loadPost(postId, false)
    setShowComments(true)
  }

  useEffect(() => {
    const wanted = commentTarget.current
    if (!wanted || !showComments) return undefined
    let tries = 0
    const tick = () => {
      if (scrollToComment(wanted) || tries > 24) return
      tries += 1
      window.setTimeout(tick, 150)
    }
    tick()
    return undefined
  }, [showComments, Comments.length])

  const goBack = () => {
    const target = leavePath(location)
    if (target && !String(target).startsWith('/post/')) {
      navigate(target)
      return
    }
    navigate(-1)
  }

  return (
    <section className="PostPage">
      <Helmet>
        <title>{activePost.title || fallbackPost.title || 'Post'}</title>
        <meta name="description" content={activePost.description || fallbackPost.description || 'Photo post'} />
        <link rel="canonical" href={`https://pinweelo.com/post/${encodeURIComponent(id)}`} />
      </Helmet>
      {promptEl}
      <header className="postTopBar">
        <button type="button" onClick={goBack} aria-label="Back"><IoIosArrowBack /></button>
        <strong>Post</strong>
        <span />
      </header>

      {!ready && !slides.length ? (
        <div className="postPageLoad">
          <SpinerLoad compact />
        </div>
      ) : (
        <div className="postFeed" ref={scroller} onScroll={onScroll}>
          {slides.map(item => (
            <PostSlide
              key={item.id}
              post={item}
              requireAuth={requireAuth}
              onOpenComments={openComments}
            />
          ))}
        </div>
      )}

      {showComments && (
        <div className="postComments">
          <div className="postCommentsHead">
            <strong>Comments</strong>
            <button type="button" onClick={() => setShowComments(false)}>Close</button>
          </div>
          <div className="postCommentsList">
            {rootComments.length === 0 && <p className="commentsEmpty">Be the first to comment</p>}
            {rootComments.map((comment, index) => (
              <CommentThread
                key={comment.id}
                comment={comment}
                repliesByParent={repliesByParent}
                onReply={comment => requireAuth() && setReplyTo(comment)}
                onVote={(type, commentId) => requireAuth() && dispatch(normalActions.add_to_comupds(type, userid, commentId))}
                onDelete={handleDeleteComment}
                canDelete={item => String(item.userid) === String(userid)}
                isLast={index === rootComments.length - 1}
              />
            ))}
          </div>
          <CommentComposer
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            userPhoto={userPhoto}
            userEmail={userEmail}
            onFocusAuth={() => requireAuth()}
            onSubmit={handleAddComment}
          />
        </div>
      )}
    </section>
  )
}

export default PostPage
